import { File as NativeFile } from '@ionic-native/file';
import { UUID } from 'angular2-uuid';
import * as moment from 'moment';
import { AppConfig } from '../../../app/app.config';
import { FileSystemHelper } from '../../../app/helpers/file-system.helper';
import { CordovaFileHelper } from '../../../app/helpers/file-system/cordova-file.helper';
import { FileHelper } from '../../../app/helpers/file-system/file.helper';
import { MediaPickerItemModel } from '../../../app/modals-popovers/media-picker-modal/media-picker-item.model';
import { IJobDataArgs, JobData } from '../job-data/job-data.model';
import { FileItemConfig, IFileItemConfigArgs } from './file-config.model';

export interface IFileArgs extends IJobDataArgs {
    name: string;
    fileSize?: number;
    mimeType?: string;
    original?: string;
    edited?: string;
    job: string;
    sequence?: number;
    config?: IFileItemConfigArgs;
    imageDataUrl?: string;
    localFileSystemPath?: string;
}

const defaults = () => ({
    name: '',
    fileSize: 0,
    mimeType: '',
    original: undefined,
    edited: undefined,
    job: '',
    sequence: 0,
    config: {},
    imageDataUrl: undefined,
    localFileSystemPath: undefined
} as IFileArgs);

export class File extends JobData {
    name: string;
    fileSize: number;
    mimeType: string;
    original: string;
    edited: string;
    job: string;
    sequence: number;
    config: FileItemConfig;
    imageDataUrl: string;
    localFileSystemPath: string;

    static async fromBlob(
        blob: Blob,
        props: IFileArgs = {} as IFileArgs
    ): Promise<File> {
        const mimeType = await FileHelper.getMimeTypeForBlob(blob);
        return new File({
            ...props,
            config: new FileItemConfig({
                ...props.config
            }),
            fileSize: blob.size,
            localFileSystemPath: FileSystemHelper.blobToObjectUrl(blob),
            mimeType,
            // tslint:disable-next-line:no-string-literal
            name: blob['name']
        });
    }

    /**
     * This method accepts a dataUri string and returns and instance of model
     *
     * @param dataUri {string}
     * @param accountId {string}
     * @param jobId {string}
     * @param nativeFileService {NativeFile}
     * @param isMobile {boolean} this argument determines which value we use for
     * localFileSystemPath in the File - either we use the cordova file path
     * or the dataUri. The reason we want to be able to control this is because
     * during development (on desktop) FileUploaderDesktopHelper is used but it
     * cannot handle a cordova file path, thus we pass a dataUri which it can
     * handle. In practice this method is only used in mobile context.
     */
    static async fromDataUri(
        dataUri: string,
        accountId: string,
        jobId: string,
        nativeFileService: NativeFile,
        isMobile = true
    ): Promise<File> {

        const id = UUID.UUID();

        const directoryEntry = await nativeFileService.resolveDirectoryUrl(
            nativeFileService.dataDirectory
        );

        const fileEntry = await nativeFileService.getFile(
            directoryEntry,
            id,
            {
                create: true,
                exclusive: false
            }
        );

        const blob = FileHelper.dataUrlToBlob(dataUri);

        await CordovaFileHelper.write(
            fileEntry,
            blob
        );

        const metaData = await CordovaFileHelper.getMetaData(fileEntry);

        const name = FileHelper.makeImageName();

        const mimeType = FileHelper.dataUrlToMime(dataUri);

        return new File({
            id,
            account: accountId,
            job: jobId,
            config: new FileItemConfig({
                group: File.mimeTypeToGroup(mimeType)
            }),
            fileSize: metaData.size,
            imageDataUrl: dataUri,
            localFileSystemPath: isMobile ?
                fileEntry.toInternalURL() :
                dataUri,
            mimeType,
            name
        });
    }

    static async fromNativeUrl(
        accountId: string,
        jobId: string,
        mimeType: string,
        name: string,
        nativeUrl: string,
        size: number
    ): Promise<File> {
        return new File({
            account: accountId,
            config: new FileItemConfig({
                group: File.mimeTypeToGroup(mimeType)
            }),
            fileSize: size,
            job: jobId,
            localFileSystemPath: nativeUrl,
            mimeType,
            name
        });
    }

    static async fromMediaPickerItemModel(
        model: MediaPickerItemModel,
        props: IFileArgs = {} as IFileArgs
    ): Promise<File> {
        return new File({
            ...props,
            config: new FileItemConfig({
                ...props.config,
                group: File.mimeTypeToGroup(model.mimeType)
            }),
            fileSize: model.size,
            localFileSystemPath: model.nativeUrl,
            mimeType: model.mimeType,
            name: model.name
        });
    }

    static mimeTypeToGroup(mimeType: string = ''): string {
        return mimeType && mimeType.startsWith('image') ? 'photo' : 'other';
    }

    constructor(data?: IFileArgs) {
        super({...defaults(), ...data});

        if (data && typeof data.config === 'string') {
            this.config = FileItemConfig.fromJson(data.config);
        }
    }
}



// WEBPACK FOOTER //
// ./src/vas/business-objects/file/file.model.ts